import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import { SwipeLeftAlt, OpenInNew } from '@mui/icons-material';
import { getComparator } from '../../utils/tableSort';
import { formatCurrency } from '../../utils/formatters';

const columns = [
  { id: 'retailer_name', label: 'Retailer', align: 'left', format: (v) => v, sx: { minWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } },
  { id: 'orders_acquired', label: 'Orders acq.', align: 'right', format: (v) => (v || 0).toLocaleString() },
  { id: 'orders_sent', label: 'Orders sent', align: 'right', format: (v) => (v || 0).toLocaleString() },
  { id: 'revenue_generated', label: 'Revenue', align: 'right', format: formatCurrency },
  { id: 'acquisition_spend', label: 'Acq. spend', align: 'right', format: formatCurrency },
  { id: 'affiliate_revenue_earned', label: 'Aff. earned', align: 'right', format: formatCurrency },
  { id: 'net_acquisition_cost', label: 'Net cost', align: 'right', format: formatCurrency },
  { id: 'cpo', label: 'CPO', align: 'right', format: formatCurrency },
  { id: 'roas', label: 'ROAS', align: 'right', format: (v) => v != null ? `${v.toFixed(2)}x` : '—' },
  { id: 'aov', label: 'AOV', align: 'right', format: formatCurrency },
];


const RetailerPerformanceTable = ({ retailers, onDrilldown }) => {
  const [orderBy, setOrderBy] = useState('revenue_generated');
  const [order, setOrder] = useState('desc');

  const handleSort = (columnId) => {
    const isDesc = orderBy === columnId && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(columnId);
  };

  const sorted = useMemo(
    () => [...(retailers || [])].sort(getComparator(order, orderBy)),
    [retailers, order, orderBy],
  );

  if (!retailers || retailers.length === 0) {
    return (
      <Paper sx={{ p: 2 }} elevation={1}>
        <Typography variant="h6" gutterBottom>Retailer performance</Typography>
        <Typography color="text.secondary">No retailer data for this period.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 1, sm: 2 } }} elevation={1}>
      <Typography variant="h6" gutterBottom>Retailer performance</Typography>
      <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5, justifyContent: 'flex-end', mb: 0.5 }}>
        <SwipeLeftAlt sx={{ fontSize: 16, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.disabled">Scroll for more</Typography>
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' }, '& td:not(:first-of-type), & th:not(:first-of-type)': { px: 1 } }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align} sx={col.sx}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'desc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right" sx={{ px: 1 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.retailer_id} hover>
                {columns.map((col) => (
                  <TableCell key={col.id} align={col.align} sx={col.sx}>
                    {col.format(row[col.id])}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ px: 0.5 }}>
                  <Tooltip title={`Partner breakdown for ${row.retailer_name}`}>
                    <IconButton size="small" onClick={() => onDrilldown?.(row.retailer_id, row.retailer_name)}>
                      <OpenInNew sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default RetailerPerformanceTable;
